import { createBrowserRouter } from "react-router-dom";
import { lazy, Suspense } from "react";
import AppLayout from "./AppLayout";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import Userprotectedroutes from "./components/ProtectedRoute/Userprotectedroutes";
import { HomeRedirect } from "./HomeRedirect";
import Loader from "./utils/Loader";
import Market from "./pages/OthersMarket/Market";
import ForgotPasswordModal from "./components/Auth/ForgotPasswordModal";
import Multipin from "./pages/Multipin.jsx/Multipin";
import { SelectUserLockApplications } from "./utils/helper/commonSelectors";
import { useSelector } from "react-redux";
import SignupProtectedRoute from "../CommonComponents/SignupProtectedRoute";
import ScoreIframe from "./pages/ScoreIframe";
import SportsTv from "../CommonComponents/SportsTv";
import TvNotFound from "./pages/TVnotFound";

// Lazy-loaded components
const NotFound = lazy(() => import("./NotFound"));
const DemoAccount = lazy(() => import("./pages/Demoaccount/DemoAccount"));
const InPlay = lazy(() => import("./pages/InPlay"));
const Election = lazy(() => import("./pages/Election"));
const Kabaddi = lazy(() => import("./pages/Kabaddi"));
const WcWinner = lazy(() => import("./pages/WcWinner"));
const Cricket = lazy(() => import("./pages/Cricket"));
const Soccer = lazy(() => import("./pages/Soccer"));
const Tennis = lazy(() => import("./pages/Tennis"));
const Horse = lazy(() => import("./pages/Horse"));
const Greyhound = lazy(() => import("./pages/Greyhound"));
const IndianPoker = lazy(() => import("./pages/IndianPoker"));
const Spribe = lazy(() => import("./pages/Spribe"));
const AeSexy = lazy(() => import("./pages/AeSexy"));
const Ezugi = lazy(() => import("./pages/Ezugi"));
const Evolution = lazy(() => import("./pages/Evolution"));
const LiveCasino = lazy(() => import("./pages/LiveCasino/LiveCasino"));
const InternationalCasino = lazy(() => import("./pages/InternationalCasino"));
const CasinoList = lazy(() => import("./pages/CasinoList"));
const Vivo = lazy(() => import("./pages/Vivo"));
const BetGames = lazy(() => import("./pages/BetGames"));
const CasinoIII = lazy(() => import("./pages/CasinoIII"));
const Profile = lazy(() => import("./pages/Account/Profile"));
const Deposit = lazy(() => import("./pages/Deposit"));
const Withdrawal = lazy(() => import("./pages/Withdrawal"));
const AccountStatement = lazy(() => import("./pages/Account/Accout-Statement"));
const ProfitLossReport = lazy(() =>
  import("./pages/Account/Profit-Loss-Report")
);
const BetHistory = lazy(() => import("./pages/Account/Bet-History"));
const UnsettledBet = lazy(() => import("./pages/Account/Unsettled-Bet"));
const ChangePassword = lazy(() => import("./pages/Account/Change-Password"));
const Rules = lazy(() => import("./pages/Account/Rules"));
const TransactionReport = lazy(() => import("./pages/Account/Transaction-Report"));
const SetStake = lazy(() => import("./pages/Account/Set-Stake"));
const Results = lazy(() => import("./pages/Account/Results"));
const UserAccount = lazy(() => import("./pages/UserAccount/index"));
const FullMarket = lazy(() => import("./pages/FullMarket/FullMarket"));
const PlayCasino = lazy(() => import("./pages/UniqueCasino/PlayCasino"));
const GreyhoundMarket = lazy(() => import("./pages/Greyhound/GreyhoundMarket"));
const HorseMarket = lazy(() => import("./pages/Horse/HorseMarket"));
const GameLobby = lazy(() => import("./pages/CasinoList/GameLobby"));
// const TvNotFound = lazy(() => import("./pages/TVnotFound"));

// Loading fallback component
const LoadingFallback = () => <Loader />;

// Route configuration
export const AsianExchangeRoutes = () => {
  // const lockData = useSelector(SelectUserLockApplications()); // Removed direct function call
  const lockData = useSelector(SelectUserLockApplications());
  const sportLockStatus = {
    cricket: lockData?.cricket ?? false, // Added fallback
    soccer: lockData?.soccer ?? false,
    tennis: lockData?.tennis ?? false,
    kabaddi: lockData?.kabaddi ?? false,
    horse: lockData?.horse ?? false,
    greyhound: lockData?.greyhound ?? false,
  };

  return createBrowserRouter([
    {
      path: "/score",
      element: (
        <ScoreIframe />
      ),
    },
    {
      path: "/game-lobby",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <GameLobby />
        </Suspense>
      ),
    },
    {
      path: "/tv-not-found",
      element: (
        <TvNotFound />
      ),
    },
    {
      path: "/tv",
      element: (
        <SportsTv />
      ),
    },
    {
      path: "/",
      element: <AppLayout />,
      children: [
        { path: "/", element: <HomeRedirect /> },
        {
          path: "/in-play",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <InPlay />
            </Suspense>
          ),
        },
        {
          path: "/election",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Election />
            </Suspense>
          ),
        },
        {
          path: "/kabaddi",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Kabaddi />
            </Suspense>
          ),
        },
        {
          path: "/forgot-password", // Consistent naming
          element: <ForgotPasswordModal />,
        },
        {
          path: "/new-casino",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <LiveCasino />
            </Suspense>
          ),
        },
        {
          path: "/play-casino-new/:eventId/:casinoType",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <PlayCasino />
            </Suspense>
          ),
        },
        {
          path: "/wc-winner-2024",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WcWinner />
            </Suspense>
          ),
        },
        ...(!sportLockStatus.cricket
          ? [
            {
              path: "/cricket",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <Cricket />
                </Suspense>
              ),
            },
          ]
          : []),
        ...(!sportLockStatus.soccer
          ? [
            {
              path: "/soccer",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <Soccer />
                </Suspense>
              ),
            },
          ]
          : []),
        ...(!sportLockStatus.tennis
          ? [
            {
              path: "/tennis",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <Tennis />
                </Suspense>
              ),
            },
          ]
          : []),
        ...(!sportLockStatus.horse
          ? [
            {
              path: "/horse",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <Horse />
                </Suspense>
              ),
            },
          ]
          : []),
        ...(!sportLockStatus.greyhound
          ? [
            {
              path: "/greyhound",
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <Greyhound />
                </Suspense>
              ),
            },
          ]
          : []),
        {
          path: "/indian-poker",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <IndianPoker />
            </Suspense>
          ),
        },
        {
          path: "/spribe",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Spribe />
            </Suspense>
          ),
        },
        {
          path: "/ezugi",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Ezugi />
            </Suspense>
          ),
        },
        {
          path: "/ae-sexy",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <AeSexy />
            </Suspense>
          ),
        },
        {
          path: "/evolution",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Evolution />
            </Suspense>
          ),
        },
        {
          path: "/live-casino",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <LiveCasino />
            </Suspense>
          ),
        },
        {
          path: "/international-casino",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <InternationalCasino />
            </Suspense>
          ),
        },
        {
          path: "/casino-list", // Uncommented and ensured consistency
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CasinoList />
            </Suspense>
          ),
        },
        {
          path: "/vivo",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Vivo />
            </Suspense>
          ),
        },
        {
          path: "/betgames",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BetGames />
            </Suspense>
          ),
        },
        {
          path: "/casino-iii",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CasinoIII />
            </Suspense>
          ),
        },
        {
          path: "/demo",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <UserAccount />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "/user",
          element: (
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <UserAccount />
              </Suspense>
            </ProtectedRoute>
          ),
        },
        {
          path: "/full-market", // Consistent naming
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <FullMarket />
            </Suspense>
          ),
        },
        {
          path: "/multi-market", // Consistent naming
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Multipin />
            </Suspense>
          ),
        },
        {
          path: "/market", // Consistent naming
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Market />
            </Suspense>
          ),
        },
        {
          path: "/horse-market", // Consistent naming
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <HorseMarket />
            </Suspense>
          ),
        },
        {
          path: "/greyhound-market", // Consistent naming
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <GreyhoundMarket />
            </Suspense>
          ),
        },
        {
          path: "/deposit",
          element: (
            <SignupProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <Deposit />
              </Suspense>
            </SignupProtectedRoute>
          ),
        },
        {
          path: "/withdraw",
          element: (
            <SignupProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <Withdrawal />
              </Suspense>
            </SignupProtectedRoute>
          ),
        },
        {
          path: "/profile",
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <Profile />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/account-statement", // Consistent naming
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <AccountStatement />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/transactions-report", // Consistent naming
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <TransactionReport />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/profit-loss-report", // Consistent naming
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <ProfitLossReport />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/bet-history", // Consistent naming
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <BetHistory />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/unsettled-bet", // Consistent naming
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <UnsettledBet />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/set-stake", // Consistent naming
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <SetStake />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/rules",
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <Rules />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/change-password", // Consistent naming
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <ChangePassword />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "/results",
          element: (
            <Userprotectedroutes>
              <Suspense fallback={<LoadingFallback />}>
                <Results />
              </Suspense>
            </Userprotectedroutes>
          ),
        },
        {
          path: "*",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </Suspense>
          ),
        },
      ],
    },
  ]);
};